/**
 * Move focus to the first invalid control after submit validation failure.
 *
 * Preference order:
 * 1. `preferredFieldIds` (caller field order) — focused when marked invalid **or**
 *    when listed in `knownInvalidIds` (from the validation result before paint)
 * 2. `[aria-invalid="true"]`
 * 3. `.error` / `[data-invalid="true"]` class markers
 * 4. native `:invalid` (required / constraint validation)
 */
const INVALID_SELECTOR = [
  '[aria-invalid="true"]',
  'input.error',
  'textarea.error',
  'select.error',
  '[data-invalid="true"]',
  'input:invalid',
  'textarea:invalid',
  'select:invalid',
].join(', ');

function isFocusableControl(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  if (el.getAttribute('disabled') != null || el.getAttribute('aria-disabled') === 'true') {
    return false;
  }
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

function isMarkedInvalid(el: HTMLElement): boolean {
  const ariaInvalid = el.getAttribute('aria-invalid') === 'true';
  const classInvalid =
    el.classList.contains('error') || el.getAttribute('data-invalid') === 'true';
  const nativeInvalid =
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
      ? !el.validity.valid
      : false;
  return ariaInvalid || classInvalid || nativeInvalid;
}

export interface FocusFirstInvalidOptions {
  /**
   * Preferred field ids that the caller already knows are invalid
   * (from the validation result). Used so focus works before React re-paints
   * aria-invalid markers.
   */
  readonly knownInvalidIds?: readonly string[];
}

/**
 * Focus the first invalid field under `root`. Returns the focused element, or null.
 */
export function focusFirstInvalid(
  root: ParentNode | null | undefined,
  preferredFieldIds?: readonly string[],
  options?: FocusFirstInvalidOptions,
): HTMLElement | null {
  if (!root) {
    return null;
  }

  const known = new Set(options?.knownInvalidIds ?? []);

  if (preferredFieldIds) {
    for (const id of preferredFieldIds) {
      if (!id) {
        continue;
      }
      const el = root.querySelector(`#${CSS.escape(id)}`);
      if (!el || !isFocusableControl(el)) {
        continue;
      }
      if (known.has(id) || isMarkedInvalid(el)) {
        el.focus();
        return el;
      }
    }
  }

  const candidates = root.querySelectorAll(INVALID_SELECTOR);
  for (const candidate of candidates) {
    if (!isFocusableControl(candidate)) {
      continue;
    }
    candidate.focus();
    return candidate;
  }

  return null;
}
